import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Coffee, Clock3, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/orders/StatusBadge';
import { PaymentBadge, SourceBadge } from '@/components/orders/OrderMetaBadges';
import { CancelReasonDialog } from '@/components/orders/CancelReasonDialog';
import { formatUZS } from '@/lib/money';
import { formatRelative, type LocaleKey } from '@/lib/time';
import { cn } from '@/lib/utils';
import { drinkItems, itemQty } from '@/lib/orderItems';
import { useUIStore } from '@/stores/uiStore';
import { useAcceptOrder, useCompleteOrder, useMarkOrderPaid } from '@/hooks/useOrderActions';
import { useNowTick } from '@/hooks/useNowTick';
import { useOrderActionQueued } from '@/hooks/useQueuedActions';
import type { OrderDTO } from '@/types/api';

interface OrderCardProps {
  order: OrderDTO;
  isNew: boolean;
  onOpen: (id: number) => void;
}

export function OrderCard({ order, isNew, onOpen }: OrderCardProps) {
  const { t } = useTranslation();
  const lang = useUIStore((s) => s.language) as LocaleKey;
  const accept = useAcceptOrder();
  const complete = useCompleteOrder();
  const markPaid = useMarkOrderPaid();
  const [cancelOpen, setCancelOpen] = useState(false);
  // Shared board-level clock (one interval for all cards) drives the elapsed-age
  // recompute below, instead of every card owning its own 30s timer.
  const now = useNowTick();

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    accept.mutate(
      { orderId: order.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.accepted', { id: order.id })),
        onError: (err) => {
          if (err.status === 409) toast(t('orders.toasts.raceLost', { id: order.id }));
          else toast.error(t('orders.toasts.actionFailed', { message: err.message }));
        },
      },
    );
  };

  const handleMarkPaid = (e: React.MouseEvent) => {
    e.stopPropagation();
    markPaid.mutate(
      { orderId: order.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.markedPaid', { id: order.id })),
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    complete.mutate(
      { orderId: order.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.completed', { id: order.id })),
        onError: (err) => {
          if (err.status === 409) toast(t('orders.toasts.raceLost', { id: order.id }));
          else toast.error(t('orders.toasts.actionFailed', { message: err.message }));
        },
      },
    );
  };

  const queued = useOrderActionQueued(order.id);

  const MAX_DRINKS_SHOWN = 3;
  const drinks = drinkItems(order.items ?? []);
  const shownDrinks = drinks.slice(0, MAX_DRINKS_SHOWN);
  const extraDrinksCount = drinks.length - shownDrinks.length;

  // Count drink UNITS, not rows — a single "Latte ×3" line is 3 items.
  const itemCount = drinks.reduce((n, d) => n + itemQty(d), 0);
  const itemLabel = t('orders.card.items', { count: itemCount });

  // Top-strip color matches the parent column's tone so cards read at a glance
  // even when scrolled into a unified list.
  const stripeClass =
    order.status === 'preparing'
      ? 'bg-warning'
      : order.status === 'completed'
        ? 'bg-success'
        : order.status === 'cancelled' || order.status === 'error'
          ? 'bg-muted-foreground'
          : 'bg-primary';

  const minutesPending =
    (now - new Date(order.createdAt).getTime()) / 60_000;
  const isWaiting = order.status === 'pending' || order.status === 'paid';
  const ageTier: 'fresh' | 'late' | 'urgent' =
    isWaiting && minutesPending >= 5 ? 'urgent' : isWaiting && minutesPending >= 2 ? 'late' : 'fresh';

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(order.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(order.id);
        }
      }}
      className={cn(
        'group relative w-full cursor-pointer text-left transition-all',
        'ticket overflow-hidden hover:-translate-y-0.5 hover:shadow-ticket-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        isNew && 'animate-slide-in-ticket ring-2 ring-primary/50 ring-offset-2 ring-offset-page',
        ageTier === 'late' && 'border-warning/60',
        ageTier === 'urgent' && 'border-destructive/60 ring-1 ring-destructive/30',
      )}
    >
      {/* Top strip — matches the parent column tone */}
      <div className={cn('absolute inset-x-0 top-0 h-[3px] opacity-90', stripeClass)} />

      {/* New stamp */}
      {isNew && !queued && (
        <div className="absolute right-3 top-3 z-10">
          <span className="stamp animate-pulse-stamp text-primary">★ Fresh</span>
        </div>
      )}

      {/* Queued (offline) indicator */}
      {queued && (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full border border-warning/40 bg-warning/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground">
          <Clock3 className="h-3 w-3 text-warning" />
          {t('orders.card.queued')}
        </div>
      )}

      <div className="px-5 pb-3 pt-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">N°</span>
            <span className="numerical truncate text-2xl text-foreground">{order.id}</span>
          </div>
          <div className="shrink-0 text-right">
            <div className="numerical num-tabular text-base font-medium text-foreground">
              {formatUZS(order.productPrice, lang)}
            </div>
            <div
              className={cn(
                'mt-0.5 text-[11px] uppercase tracking-wider',
                ageTier === 'urgent'
                  ? 'font-semibold text-destructive'
                  : ageTier === 'late'
                    ? 'font-semibold text-foreground'
                    : 'text-muted-foreground',
              )}
            >
              {formatRelative(order.createdAt, lang)}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          <StatusBadge status={order.status} />
          <SourceBadge source={order.source} />
          <PaymentBadge isPaid={order.isPaid} source={order.source} />
          {order.tableName && (
            <span className="truncate text-xs font-semibold tracking-wide text-foreground">
              · {order.tableName}
            </span>
          )}
          {order.customerPhone && (
            <span className="num-tabular truncate text-xs font-medium tracking-wide text-muted-foreground">
              · {order.customerPhone}
            </span>
          )}
        </div>
      </div>

      <div className="dotted-rule mx-5" />

      <div className="flex items-center gap-3 px-5 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-primary">
          <Coffee className="h-4 w-4" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          {drinks.length === 0 ? (
            <div className="line-clamp-1 text-[15px] font-medium text-foreground">{order.drinkName}</div>
          ) : (
            <div className="space-y-0.5">
              {shownDrinks.map((drink) => (
                <div key={drink.id} className="line-clamp-1 text-[15px] font-medium text-foreground">
                  {drink.name}
                  {itemQty(drink) > 1 && <span className="text-muted-foreground"> ×{itemQty(drink)}</span>}
                </div>
              ))}
              {extraDrinksCount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {t('orders.card.moreDrinks', { count: extraDrinksCount })}
                </div>
              )}
            </div>
          )}
          <div className="text-xs text-muted-foreground">{itemLabel}</div>
        </div>
      </div>

      {order.source === 'onecafe' && order.status === 'completed' && order.isPaid === false && (
        <div className="border-t border-dashed border-border bg-muted/30 p-3">
          <Button
            onClick={handleMarkPaid}
            disabled={markPaid.isPending}
            size="lg"
            variant="success"
            className="w-full"
          >
            {t('orders.actions.markPaid')}
          </Button>
        </div>
      )}

      {(order.status === 'pending' ||
        order.status === 'paid' ||
        order.status === 'preparing') && (
        <div className="flex gap-2 border-t border-dashed border-border bg-muted/30 p-3">
          {order.status === 'pending' || order.status === 'paid' ? (
            <Button
              onClick={handleAccept}
              disabled={accept.isPending}
              size="lg"
              variant="default"
              className="flex-1"
            >
              {t('orders.actions.accept')}
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={complete.isPending}
              size="lg"
              variant="success"
              className="flex-1"
            >
              {t('orders.actions.complete')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('orders.actions.cancel')}
            className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setCancelOpen(true);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

    </div>
    <CancelReasonDialog
      orderId={order.id}
      open={cancelOpen}
      onOpenChange={setCancelOpen}
    />
    </>
  );
}
